import type {
  CogniteSourceable,
  CogniteSourceableProperties,
  InFieldUser,
} from '@cognite/apm-client';

export type SourceCreatedFields = {
  createdBy: undefined;
} & Pick<CogniteSourceable, 'sourceCreatedTime' | 'sourceCreatedUser'>;

export type SourceUpdatedFields = {
  updatedBy: undefined;
} & Pick<CogniteSourceable, 'sourceUpdatedTime' | 'sourceUpdatedUser'>;

/**
 * Returns the source fields for tracking data creation.
 *
 * SECURITY: `createdBy` is explicitly set to undefined to prevent user identity
 * data from being written to data modeling fields. This is a deliberate control
 * following an audit finding where user PII was stored into data model storage.
 * Do NOT remove the `createdBy: undefined` — its presence in the spread
 * overrides any previously set value on the target object.
 */
export const getSourceCreatedFields = (
  user: Pick<InFieldUser, 'externalId'>
): SourceCreatedFields => ({
  createdBy: undefined,
  sourceCreatedTime: new Date().toISOString(),
  sourceCreatedUser: user.externalId,
});

/**
 * Returns the source fields for tracking data updates.
 *
 * SECURITY: `updatedBy` is explicitly set to undefined to prevent user identity
 * data from being written to data modeling fields. This is a deliberate control
 * following an audit finding where user PII was stored into data model storage.
 * Do NOT remove the `updatedBy: undefined` — its presence in the spread
 * overrides any previously set value on the target object.
 */
export const getSourceUpdatedFields = (
  user: Pick<InFieldUser, 'externalId'>
): SourceUpdatedFields => ({
  updatedBy: undefined,
  sourceUpdatedTime: new Date().toISOString(),
  sourceUpdatedUser: user.externalId,
});

/**
 * Extracts CogniteSourceable properties from any object.
 * Use this utility to map sourceable fields for upsert operations.
 *
 * `sourceCreatedUser` and `sourceUpdatedUser` are the user's externalId
 * from userProfile and are safe to store in data modeling.
 */
export const extractSourceableFields = <T extends Partial<CogniteSourceable>>(
  data: T
): CogniteSourceableProperties => ({
  ...(data.sourceCreatedTime !== undefined && {
    sourceCreatedTime: data.sourceCreatedTime,
  }),
  ...(data.sourceUpdatedTime !== undefined && {
    sourceUpdatedTime: data.sourceUpdatedTime,
  }),
  ...(data.sourceCreatedUser !== undefined && {
    sourceCreatedUser: data.sourceCreatedUser,
  }),
  ...(data.sourceUpdatedUser !== undefined && {
    sourceUpdatedUser: data.sourceUpdatedUser,
  }),
  ...(data.sourceId !== undefined && { sourceId: data.sourceId }),
  ...(data.sourceContext !== undefined && {
    sourceContext: data.sourceContext,
  }),
  ...(data.source !== undefined && { source: data.source }),
});
